import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  CSVExportService,
  CardComponent,
  FormatDatetimeTimezonePipe,
  FormatNumberPipe,
  FormatterService,
  MessengerService,
  __,
  blueZodiac,
  cantaloupe,
  cornflower,
  grey,
  pastelGreen,
  randomString,
} from '@web/birdz-angular';
import { LegendItemComponent } from 'app/components/legend-item/legend-item.component';
import { ModalHelpEquippingComponent } from 'app/components/modal-help-equipping/modal-help-equipping.component';
import { fontFamily } from 'app/config/constants.config';
import { standardColors } from 'app/config/standard-colors.config';
import { EquippingStats } from 'app/interfaces/equipping-stats.interface';
import { UserService } from 'app/services/user.service';
import { format } from 'date-fns';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { Observable, map, tap } from 'rxjs';

@Component({
  selector: 'app-card-equipping',
  templateUrl: './card-equipping.component.html',
  styleUrls: ['./card-equipping.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    HighchartsChartComponent,
    TranslocoPipe,
    FormatNumberPipe,
    FormsModule,
    FormatDatetimeTimezonePipe,
    CardComponent,
    MatSlideToggleModule,
    MatTooltipModule,
    LegendItemComponent,
    AsyncPipe,
  ],
})
export class CardEquippingComponent implements OnChanges, OnInit {
  private transloco = inject(TranslocoService);
  private formatter = inject(FormatterService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private csvExport = inject(CSVExportService);
  private messenger = inject(MessengerService);

  private destroyRef = inject(DestroyRef);
  isRemoteReadView = false;
  isDetailedView = false;
  detailedEquippedMetersLegendItems: {
    id: string;
    title: string;
  }[] = [];
  detailedEquippedMetersLegendChildren: string[] = [];
  @Input() equippingStats?: EquippingStats | null = null;
  @Input() equippingLastModified?: Date | null = null;
  @Input({ required: true }) hasPerimeter?: boolean;
  @Input({ required: true }) isAvailableForSelectedTecho?: boolean;
  @Input({ required: true }) isLoading?: boolean;
  chartOptions?: Highcharts.Options | null;
  chart?: Highcharts.Chart;
  Highcharts = Highcharts;
  updateFlag = false;
  isBirdzAdmin$: Observable<boolean>;
  categoryColors: Record<string, string> = {
    EQUIPPED: cornflower,
    NON_EQUIPPED: cantaloupe,
  };

  constructor() {
    this.isBirdzAdmin$ = this.userService.user$.pipe(map(() => this.userService.isBirdzAdmin()));
  }

  ngOnInit() {
    this.handleLanguageChange();
  }

  ngOnChanges() {
    this.updateChart();
  }

  subtitle = (count?: number, total?: number) => {
    count = count ?? 0;

    const METERS = __('METERS', { count });
    const percentage = total != null ? (count / total) * 100 : Infinity;
    return `${this.formatter.number(count)} ${METERS} - ${this.formatter.number(percentage, '0.0-1')}%`;
  };

  handleLanguageChange() {
    this.transloco.langChanges$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updateChart())
      )
      .subscribe();
  }

  toggleView() {
    this.updateChart();
  }

  resetChart() {
    this.chartOptions = null;
  }

  help() {
    this.dialog.open(ModalHelpEquippingComponent, { maxWidth: '900px' });
  }

  setChartInstance(chart: Highcharts.Chart) {
    this.chart = chart;
  }

  getSortedEquippedByTypeEntries(state: string) {
    return Object.entries(this.equippingStats?.pds_equipped_by_state_type[state] ?? {}).sort((a, b) =>
      a[1] < b[1] ? 1 : -1
    ); // sort count descending
  }

  getEquippedMetersLegendChildren(): string[] {
    return this.isRemoteReadView
      ? ['equipped_meters_fix_network', 'equipped_meters_walk_by', 'non_birdz_device_meters']
      : this.isDetailedView
        ? this.detailedEquippedMetersLegendChildren
        : [];
  }

  getData() {
    const equippedMeters = {
      id: 'equipped_meters',
      name: __('EQUIPPED_METERS'),
      color: this.categoryColors['EQUIPPED'],
      y: this.equippingStats?.pds_equipped,
    };

    const equippedMetersFN = {
      id: 'equipped_meters_fix_network',
      name: __('EQUIPPED_METERS_FIX_NETWORK'),
      color: pastelGreen,
      y: this.equippingStats?.pds_equipped_fix_network,
    };

    const equippedMetersWB = {
      id: 'equipped_meters_walk_by',
      name: __('EQUIPPED_METERS_WALK_BY'),
      color: blueZodiac,
      y: this.equippingStats?.pds_equipped_walk_by,
    };

    const state = 'EN_SERVICE';
    this.detailedEquippedMetersLegendItems = [];
    this.detailedEquippedMetersLegendChildren = [];
    const equippedMetersByStateType: {
      id: string;
      name: string;
      color: string;
      y: number;
    }[] = [];
    if (this.equippingStats?.pds_equipped_by_state_type[state]) {
      this.getSortedEquippedByTypeEntries(state).forEach(([type, count], index) => {
        const id = 'equipped_meters_' + state + '_' + type;

        equippedMetersByStateType.push({
          id: id,
          name: __(type),
          color: standardColors[index],
          y: count,
        });

        this.detailedEquippedMetersLegendChildren.push(id);
        this.detailedEquippedMetersLegendItems.push({
          id: id,
          title: __(type),
        });
      });
    }

    const nonBirdzDeviceMeters = {
      id: 'non_birdz_device_meters',
      name: __('NON_BIRDZ_DEVICE_METERS'),
      color: grey,
      y: this.equippingStats?.pds_not_birdz,
    };
    this.detailedEquippedMetersLegendChildren.push(nonBirdzDeviceMeters.id);

    const nonEquippedMeters = {
      id: 'non_equipped_meters',
      name: __('NON_EQUIPPED_METERS'),
      color: this.categoryColors['NON_EQUIPPED'],
      y: this.equippingStats?.pds_not_equipped,
    };

    if (this.userService.isBirdzAdmin() && this.isRemoteReadView) {
      return [equippedMetersFN, equippedMetersWB, nonBirdzDeviceMeters, nonEquippedMeters];
    }

    return this.isDetailedView
      ? [...equippedMetersByStateType, nonBirdzDeviceMeters, nonEquippedMeters]
      : [equippedMeters, nonEquippedMeters];
  }

  updateChart() {
    if (!this.equippingStats) {
      return this.resetChart();
    }

    const data = this.getData();
    this.chartOptions = {
      credits: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        type: 'pie',
        style: {
          fontFamily: fontFamily,
        },
      },
      tooltip: {
        pointFormat: `${__('COUNT')}${__('COLON')}{point.y}`,
      },
      lang: {
        noData: '',
      },
      noData: {},
      legend: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          type: 'pie',
          data: data,
        },
      ],
    };
    setTimeout(() => {
      if (this.chartOptions) {
        this.chart?.update(this.chartOptions);
        this.updateFlag = !this.updateFlag;
      }
    });
  }

  export() {
    if (!this.equippingStats) {
      this.messenger.error(__('NO_DATA_TO_EXPORT'));
      return;
    }

    const data: (string | number | null)[][] = [];
    const lastModified = this.formatter.datetime(this.equippingLastModified ?? new Date());
    const getPercentage = (count: number) =>
      Math.round((count / (this.equippingStats!.pds_total ?? 1)) * 100 * 10) / 10;

    data.push([__('DATE'), __('ENTITY_NAME'), __('MODEL_FAMILY'), __('NUMBER_OF_METERS'), __('PERCENTAGE')]);
    data.push([lastModified, __('METERS_TOTAL'), null, this.equippingStats.pds_total ?? 0, 100]);
    data.push([
      lastModified,
      __('EQUIPPED_METERS'),
      __('ALL'),
      this.equippingStats.pds_equipped ?? 0,
      getPercentage(this.equippingStats.pds_equipped ?? 0),
    ]);

    const state = 'EN_SERVICE';
    if (this.equippingStats.pds_equipped_by_state_type[state]) {
      this.getSortedEquippedByTypeEntries(state).forEach(([type, count]) => {
        data.push([lastModified, __('EQUIPPED_METERS'), __(type), count, getPercentage(count)]);
      });
    }

    if (this.equippingStats.pds_not_birdz) {
      data.push([
        lastModified,
        __('EQUIPPED_METERS'),
        __('NON_BIRDZ_DEVICE_METERS'),
        this.equippingStats.pds_not_birdz,
        getPercentage(this.equippingStats.pds_not_birdz),
      ]);
    }

    data.push([
      lastModified,
      __('NON_EQUIPPED_METERS'),
      __('ALL'),
      this.equippingStats.pds_not_equipped ?? 0,
      getPercentage(this.equippingStats.pds_not_equipped ?? 0),
    ]);

    const datetime = format(new Date(), 'yyyy_MM_dd_HH_mm_ss');
    const id = randomString(10);
    const filename = `meters_and_devices_${datetime}_${id}`;
    const separator = this.formatter.config.csvSeparator ?? ';';

    this.csvExport.downloadFile(data, filename, separator);
  }
}
